// =============================================================================
//  apiDefaultPromo.js -- client of the Default Promotional Swipers
//  (server_elakay/elakay_routes/defaultPromo.js).
//
//  Administration (Swiper Manager -> "Default Promotional Swipers", Family
//  only -- re-checked server-side) sends the Bearer token. The public read
//  used by every shop without its own promotional swiper is cached in memory
//  per (target, language, shop) with in-flight de-duplication, so a page
//  makes at most ONE request and navigating back to it costs nothing.
// =============================================================================
import { elakay_API } from "../../config";
import { isAuthenticated } from "../auth";
import { batchFiles, compressImageForUpload, uploadFeature, UPLOAD_LIMITS } from "../../utils/uploadPolicy";
import { messageForStatus } from "../../utils/uploadResponse";

const getToken = () => isAuthenticated()?.token || "";

const readJson = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

export async function apiFetch(path, { method = "GET", body, form, auth = true } = {}) {
  const headers = { Accept: "application/json" };
  const token = auth ? getToken() : "";
  if (token) headers.Authorization = `Bearer ${token}`;
  const init = { method, headers };
  if (form) {
    init.body = form; // the browser sets the multipart boundary itself
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  const response = await fetch(`${elakay_API}${path}`, init);
  const data = await readJson(response);
  if (!response.ok) {
    // A proxy answer (nginx 413 page) has no JSON body: readable message.
    const error = new Error(data.error || messageForStatus(response.status, data));
    error.status = response.status;
    error.code = data.code || (response.status === 413 ? "REQUEST_TOO_LARGE" : undefined);
    error.details = data.details;
    throw error;
  }
  return data;
}

const query = (params = {}) => {
  const clean = Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""));
  const text = new URLSearchParams(clean).toString();
  return text ? `?${text}` : "";
};

// ─── Public composition (cached) ─────────────────────────────────────────

const PUBLIC_TTL_MS = 60 * 1000;
const publicCache = new Map();

export const invalidatePublicDefaultPromo = () => publicCache.clear();

const publicKey = ({ target, lang, salt }) => `${target}|${lang}|${salt || ""}`;

// Synchronous read of a cached answer (lets a remounted banner render on
// its first frame instead of flashing its placeholder).
export const peekPublicDefaultPromo = (params) => {
  const cached = publicCache.get(publicKey(params));
  return cached && cached.expiresAt > Date.now() && cached.value ? cached.value : null;
};

export const getPublicDefaultPromo = ({ target = "shop", lang = "en", salt = "" } = {}) => {
  const key = publicKey({ target, lang, salt });
  const cached = publicCache.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached.promise;
  const entry = { expiresAt: Date.now() + PUBLIC_TTL_MS, value: null, promise: null };
  entry.promise = apiFetch(`/default-promo/public${query({ target, lang, salt })}`, { auth: false })
    .then((data) => {
      const value = { config: data.config || null, slides: Array.isArray(data.slides) ? data.slides : [], frame: data.frame || null, swiperOptions: data.swiperOptions || null, fallback: data.fallback || "legacy", renewOnLoad: data.renewOnLoad !== false, expiresAt: data.expiresAt || null };
      // The composition rotates at expiresAt: never cache past it.
      if (value.expiresAt) entry.expiresAt = Math.min(entry.expiresAt, new Date(value.expiresAt).getTime());
      entry.value = value;
      return value;
    })
    .catch(() => {
      // A failure is never cached: the next mount retries.
      publicCache.delete(key);
      return { config: null, slides: [], fallback: "legacy", error: true };
    });
  for (const [cachedKey, cachedEntry] of publicCache) {
    if (cachedEntry.expiresAt <= Date.now()) publicCache.delete(cachedKey);
  }
  while (publicCache.size >= 500) publicCache.delete(publicCache.keys().next().value);
  publicCache.set(key, entry);
  return entry.promise;
};

// ─── Administration ─────────────────────────────────────────────────────

const ids = (list) => ({ ids: list });
const done = (promise) => promise.then((data) => { invalidatePublicDefaultPromo(); return data; });

export const getDefaultPromoRegistry = () => apiFetch("/default-promo/registry");
export const previewDefaultPromo = (body) => apiFetch("/default-promo/preview", { method: "POST", body });

export const listDefaultPromoConfigs = (params) => apiFetch(`/default-promo/configs${query(params)}`);
export const getDefaultPromoConfig = (id) => apiFetch(`/default-promo/configs/${id}`);
export const createDefaultPromoConfig = (body) => done(apiFetch("/default-promo/configs", { method: "POST", body }));
export const updateDefaultPromoConfig = (id, body) => done(apiFetch(`/default-promo/configs/${id}`, { method: "PUT", body }));
export const publishDefaultPromoConfig = (id) => done(apiFetch(`/default-promo/configs/${id}/publish`, { method: "POST" }));
export const unpublishDefaultPromoConfig = (id) => done(apiFetch(`/default-promo/configs/${id}/unpublish`, { method: "POST" }));
export const discardDefaultPromoDraft = (id) => done(apiFetch(`/default-promo/configs/${id}/discard`, { method: "POST" }));
export const duplicateDefaultPromoConfig = (id) => done(apiFetch(`/default-promo/configs/${id}/duplicate`, { method: "POST" }));
export const deleteDefaultPromoConfig = (id) => done(apiFetch(`/default-promo/configs/${id}`, { method: "DELETE" }));
export const setDefaultPromoConfigsStatus = (list, status) => done(apiFetch("/default-promo/configs/status", { method: "PATCH", body: { ...ids(list), status } }));
export const bulkDeleteDefaultPromoConfigs = (list) => done(apiFetch("/default-promo/configs/bulk-delete", { method: "POST", body: ids(list) }));
export const reorderDefaultPromoConfigs = (list) => done(apiFetch("/default-promo/configs/order", { method: "PUT", body: ids(list) }));

export const listDefaultPromoImages = (params) => apiFetch(`/default-promo/images${query(params)}`);
// Heavy photos are re-encoded first; the files then travel in batches whose
// total stays under the proxy ceiling (30 MiB per request, UP-01).
export const uploadDefaultPromoImages = async (files, names = []) => {
  const list = Array.from(files);
  const prepared = await Promise.all(list.map((file) => compressImageForUpload("defaultPromoImage", file)));
  const indexed = prepared.map((file, index) => Object.assign(file, { __index: index }));
  const batches = batchFiles(indexed, { maxBytes: UPLOAD_LIMITS.proxy.defaultRequestBytes - 1024 * 1024, maxFiles: uploadFeature("defaultPromoImage").maxFiles });
  const results = [];
  for (const batch of batches) {
    const form = new FormData();
    batch.forEach((file) => form.append("files", file, file.name));
    form.append("names", JSON.stringify(batch.map((file) => names[file.__index])));
    results.push(await apiFetch("/default-promo/images", { method: "POST", form }));
  }
  invalidatePublicDefaultPromo();
  return results.length === 1 ? results[0] : results.reduce((all, result) => ({
    ...all, ...result,
    images: [...(all.images || []), ...(result.images || [])],
    errors: [...(all.errors || []), ...(result.errors || [])],
  }), {});
};
export const replaceDefaultPromoImage = async (id, file) => {
  const form = new FormData();
  file = await compressImageForUpload("defaultPromoImage", file);
  form.append("file", file, file.name);
  return done(apiFetch(`/default-promo/images/${id}/file`, { method: "PUT", form }));
};
export const updateDefaultPromoImage = (id, body) => done(apiFetch(`/default-promo/images/${id}`, { method: "PATCH", body }));
export const setDefaultPromoImagesStatus = (list, status) => done(apiFetch("/default-promo/images/status", { method: "PATCH", body: { ...ids(list), status } }));
export const reorderDefaultPromoImages = (list) => done(apiFetch("/default-promo/images/order", { method: "PUT", body: ids(list) }));
export const getDefaultPromoImageUsage = (id) => apiFetch(`/default-promo/images/${id}/usage`);
export const deleteDefaultPromoImage = (id) => done(apiFetch(`/default-promo/images/${id}`, { method: "DELETE" }));

export const listDefaultPromoTexts = (params) => apiFetch(`/default-promo/texts${query(params)}`);
export const createDefaultPromoText = (body) => done(apiFetch("/default-promo/texts", { method: "POST", body }));
export const updateDefaultPromoText = (id, body) => done(apiFetch(`/default-promo/texts/${id}`, { method: "PUT", body }));
export const duplicateDefaultPromoText = (id) => done(apiFetch(`/default-promo/texts/${id}/duplicate`, { method: "POST" }));
export const setDefaultPromoTextsStatus = (list, status) => done(apiFetch("/default-promo/texts/status", { method: "PATCH", body: { ...ids(list), status } }));
export const reorderDefaultPromoTexts = (list) => done(apiFetch("/default-promo/texts/order", { method: "PUT", body: ids(list) }));
export const deleteDefaultPromoText = (id) => done(apiFetch(`/default-promo/texts/${id}`, { method: "DELETE" }));

export const listDefaultPromoPalettes = () => apiFetch("/default-promo/palettes");
export const createDefaultPromoPalette = (body) => done(apiFetch("/default-promo/palettes", { method: "POST", body }));
export const updateDefaultPromoPalette = (id, body) => done(apiFetch(`/default-promo/palettes/${id}`, { method: "PUT", body }));
export const deleteDefaultPromoPalette = (id) => done(apiFetch(`/default-promo/palettes/${id}`, { method: "DELETE" }));

export const getDefaultPromoLegacyStatus = () => apiFetch("/default-promo/legacy/status");
export const importDefaultPromoLegacyImage = ({ blob, fileName, legacyKey, name, kind }) => {
  const form = new FormData();
  form.append("legacyKey", legacyKey);
  form.append("name", name);
  form.append("kind", kind);
  form.append("file", blob, fileName);
  return done(apiFetch("/default-promo/legacy/image", { method: "POST", form }));
};
export const importDefaultPromoLegacyTexts = (texts) => done(apiFetch("/default-promo/legacy/texts", { method: "POST", body: { texts } }));
export const ensureDefaultPromoLegacyConfig = (publish = false) => done(apiFetch("/default-promo/legacy/config", { method: "POST", body: { publish } }));
